/**
 * The RPE checklist bundle (W3-07; WORKPACKAGES Appendix C H6, DESIGN.md §12.1): one zip a person with a real
 * RPE >= 1.6 install works through, so the RPE-compatible profile can lose its "unverified" label. Built from the
 * open chart (use fixtures/synthetic-rpe-full: it has parents turning with their parent, UI lines, tint and judge
 * width, eased RPE 1.7 speed): every export goes through the io worker's exporter with the RPE-compatible profile and
 * RPE id file names, and the variants D§12.1 asks about are made from those packages:
 *
 *   1-open/                    the package as exported: does RPE open it? info.txt, numeric Path, <id>.* names
 *   2-padding-null/            eventLayers padded with null (the exporter's choice is in 1-open)
 *   2-padding-empty-object/    eventLayers padded with {}
 *   2-two-layer-slots/         lines with only 2 eventLayers slots
 *   3-unknown-keys/            unknown keys on META, lines, notes and events: which survive an RPE re-save?
 *   4-rpeversion-160/          RPEVersion 160 with the eased speed kept
 *   4-rpeversion-170/          RPEVersion 170 with the eased speed kept
 *   5-features/                rotateWithFather, attachUI, tint / judgeArea (the package of 1-open, listed here)
 *   CHECKLIST.txt              the questions, what to record, and the files of each item
 */

import { unzipSync, zipSync, strToU8 } from 'fflate';
import type { AppServices } from '../../app/registry/types';
import type { ChartDoc } from '../../core/model/types';
import type { ExportOptions } from '../../formats/types';
import type { DocStore } from '../../state/types';
import { produceExport } from './exportFlow';

/** The answer sheet of the bundle (plain text inside the zip; English, like the RPE checklist in DESIGN.md). */
export const CHECKLIST_TEXT = `Phiedit RPE checklist bundle (DESIGN.md section 12.1)
=====================================================

Needs a real RPE (Re:PhiEdit) 1.6 or later. For each item, import the .pez of its folder into RPE and write
down what you see next to the item. Send this file back with your answers; the RPE-compatible profile stays
labelled "unverified" until every item is answered.

1. 1-open: does RPE open the package? Does it need info.txt? Does it need the numeric Path and the <id>.* file
   names (the package uses both)?
   Answer:

2. Layer padding. 2-padding-null pads eventLayers with null, 2-padding-empty-object with {}, 2-two-layer-slots
   has only 2 slots per line. Which ones open, and do the lines move the same in all three?
   Answer:

3. 3-unknown-keys has extra keys "phieditCheck" on META, on every line, on the first note and the first event of
   each line. Open it in RPE, save it, then unzip the saved chart: which of those keys are still there?
   Answer:

4. 4-rpeversion-160 and 4-rpeversion-170 differ only in META.RPEVersion. Does RPE 1.7 play the eased scroll
   speed eased in both, or linear in one of them?
   Answer:

5. 5-features: do rotateWithFather (lines turning with their parent), the attachUI lines (pause, combo, score...),
   note tint and judgeArea survive a round trip through RPE (open, save, compare the chart.json)?
   Answer:
`;

type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

function isObj(v: Json | undefined): v is { [k: string]: Json } {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** The chart file of an exported package (the .json that is not info / extra / sidecar). */
export function chartPathOf(files: Readonly<Record<string, Uint8Array>>): string | null {
  const skip = new Set(['extra.json', 'phiedit.json']);
  return Object.keys(files).find((p) => p.toLowerCase().endsWith('.json') && !skip.has(p.toLowerCase())) ?? null;
}

/** Rewrites the chart JSON of a package; the other files are kept byte for byte. */
export function withChart(pez: Uint8Array, edit: (chart: { [k: string]: Json }) => void): Uint8Array {
  const files = unzipSync(pez);
  const path = chartPathOf(files);
  const bytes = path === null ? undefined : files[path];
  if (path === null || bytes === undefined) throw new Error('the package has no chart file');
  const chart = JSON.parse(new TextDecoder().decode(bytes)) as Json;
  if (!isObj(chart)) throw new Error('the chart file is not a JSON object');
  edit(chart);
  return zipSync({ ...files, [path]: strToU8(JSON.stringify(chart)) });
}

function lines(chart: { [k: string]: Json }): Array<{ [k: string]: Json }> {
  const list = chart.judgeLineList;
  return Array.isArray(list) ? list.filter(isObj) : [];
}

/** Pads or trims every line's eventLayers to `slots` entries, padding with `pad` (null or {}). */
export function padLayers(chart: { [k: string]: Json }, pad: 'null' | 'object', slots = 4): void {
  for (const line of lines(chart)) {
    const layers = Array.isArray(line.eventLayers) ? [...line.eventLayers] : [];
    const kept = layers.slice(0, slots);
    while (kept.length < slots) kept.push(pad === 'null' ? null : {});
    line.eventLayers = kept.map((l) =>
      l === null || (isObj(l) && Object.keys(l).length === 0) ? (pad === 'null' ? null : {}) : l,
    );
  }
}

/** Adds a "phieditCheck" key to META, every line, its first note and the first event of each of its layers. */
export function addUnknownKeys(chart: { [k: string]: Json }): void {
  if (isObj(chart.META)) chart.META.phieditCheck = 'meta';
  lines(chart).forEach((line, i) => {
    line.phieditCheck = `line ${String(i)}`;
    const notes = line.notes;
    if (Array.isArray(notes) && isObj(notes[0])) notes[0].phieditCheck = `note of line ${String(i)}`;
    const layers = Array.isArray(line.eventLayers) ? line.eventLayers : [];
    for (const layer of layers) {
      if (!isObj(layer)) continue;
      for (const [k, events] of Object.entries(layer)) {
        if (Array.isArray(events) && isObj(events[0])) events[0].phieditCheck = `${k} of line ${String(i)}`;
      }
    }
  });
}

export function setRpeVersion(chart: { [k: string]: Json }, version: number): void {
  if (isObj(chart.META)) chart.META.RPEVersion = version;
}

/** Folder and package of each item, plus the answer sheet. */
export async function buildRpeChecklist(
  services: Pick<AppServices, 'persist' | 'io'>,
  store: Pick<DocStore, 'projectId' | 'doc' | 'seq'>,
  base: ExportOptions,
): Promise<Uint8Array> {
  const opts: ExportOptions = {
    ...base,
    target: 'pez',
    profile: 'rpe-compatible',
    naming: isNumeric(store.doc) ? 'rpe-id' : base.naming,
    includeInfoTxt: true,
    includeSidecar: false,
    rpeVersionOverride: null,
  };
  const produced = await produceExport(services, store, opts);
  const pez = produced.result.bytes;
  if (pez.length === 0) throw new Error(produced.result.selfCheck.diffs.join('; ') || 'the export was refused');
  const name = produced.result.fileName;
  const out: Record<string, Uint8Array> = {
    'CHECKLIST.txt': strToU8(CHECKLIST_TEXT),
    [`1-open/${name}`]: pez,
    [`2-padding-null/${name}`]: withChart(pez, (c) => {
      padLayers(c, 'null');
    }),
    [`2-padding-empty-object/${name}`]: withChart(pez, (c) => {
      padLayers(c, 'object');
    }),
    [`2-two-layer-slots/${name}`]: withChart(pez, (c) => {
      padLayers(c, 'null', 2);
    }),
    [`3-unknown-keys/${name}`]: withChart(pez, addUnknownKeys),
    [`4-rpeversion-160/${name}`]: withChart(pez, (c) => {
      setRpeVersion(c, 160);
    }),
    [`4-rpeversion-170/${name}`]: withChart(pez, (c) => {
      setRpeVersion(c, 170);
    }),
    [`5-features/${name}`]: pez,
  };
  return zipSync(out, { level: 0 });
}

function isNumeric(doc: ChartDoc): boolean {
  return /^\d+$/.test(doc.meta.rpeId);
}
